import { View } from "react-native";
import { IconButton, Text, useTheme } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import { Carrier, Status, Substatus } from "./types";

type Props = {
  status: Status;
  substatus: Substatus;
  carrier: Carrier;
};

export function ProgressBar({ status, substatus, carrier }: Props) {
  const tailwind = useTailwind();

  const steps = getSteps(status, substatus, carrier);
  const currentStep = steps.indexOf(status) + 1;

  return (
    <View style={tailwind("flex-row")}>
      {steps.map((step, index) => {
        const showLeft = index !== 0;
        const showRight = index !== steps.length - 1;
        const completed = currentStep > index;
        const error = isErrorStep(step);
        const nextStepIsError = isErrorStep(steps[index + 1]);

        return (
          <View key={step} style={tailwind("flex-1 gap-3")}>
            <View style={tailwind("flex-row items-center")}>
              <Bar visible={showLeft} completed={completed} error={error} />
              <Icon completed={completed} error={error} />
              <Bar
                visible={showRight}
                completed={completed && !error}
                error={nextStepIsError}
              />
            </View>
            <Label
              status={step}
              completed={completed}
              isCurrent={currentStep === index + 1}
            />
          </View>
        );
      })}
    </View>
  );
}

function getSteps(
  status: Status,
  substatus: Substatus,
  carrier: Carrier
): Status[] {
  if (carrier === "merchant") {
    return ["pre_processing", "pre_transit", "delivered"];
  }

  switch (status) {
    case "failure": {
      if (substatus === "rejected_consumer_validation") {
        return ["pre_processing", "failure", "transit", "delivered"];
      } else {
        return ["pre_processing", "pre_transit", "failure", "delivered"];
      }
    }
    case "returned":
      return ["pre_processing", "pre_transit", "transit", "returned"];
    default:
      return ["pre_processing", "pre_transit", "transit", "delivered"];
  }
}

function isErrorStep(step: Status) {
  return step === "failure" || step === "returned";
}

function getStatusText(status: Status) {
  switch (status) {
    case "pre_processing":
      return "Ordenado";
    case "pre_transit":
      return "Confirmado";
    case "transit":
      return "Enviado";
    case "delivered":
      return "Entregado";
    case "returned":
      return "Devuelto";
    case "failure":
      return "Cancelado";
  }
}

function Label({
  status,
  completed,
  isCurrent,
}: {
  status: Status;
  completed: boolean;
  isCurrent: boolean;
}) {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  return (
    <Text
      variant="bodySmall"
      style={[
        tailwind(`text-center ${isCurrent ? "font-bold" : ""}`),
        {
          color: completed ? colors.onPrimaryContainer : colors.surfaceVariant,
        },
      ]}
    >
      {getStatusText(status)}
    </Text>
  );
}

type IconProps = Record<"completed" | "error", boolean>;
function Icon({ completed, error }: IconProps) {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  const icon = error ? "close" : completed ? "check" : "";

  const bgStyle = error
    ? { backgroundColor: colors.error }
    : completed
    ? tailwind("bg-primary-500")
    : { backgroundColor: colors.surfaceVariant };

  return (
    <IconButton
      icon={icon}
      size={16}
      iconColor={colors.onPrimary}
      style={[tailwind("text-white m-0 -mx-px z-50"), bgStyle]}
    />
  );
}

type BarProps = Record<"visible" | "completed" | "error", boolean>;
function Bar({ visible, completed, error }: BarProps) {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  const bgStyle = !visible
    ? undefined
    : error
    ? { backgroundColor: colors.error }
    : completed
    ? tailwind("bg-primary-500")
    : { backgroundColor: colors.surfaceVariant };

  return <View style={[tailwind(`flex-1 h-1`), bgStyle]} />;
}
